import { getHttpClientAsLastParam } from 'ContentData/helpers/apiHelpers';
import { CREATE_DEFAULTS } from 'ContentData/constants/apiDefaults';
import { createRpcClientV2 } from 'rpc-client-utils';
import http from 'hub-http/clients/apiClient';
import { userInfoSync } from 'hub-http/userInfo';
import { gate } from 'hub-http/gates';
import Routes from 'ContentUtils/Routes';
import { listOwnedAddressesRpc } from '../../__generated__/chirp/com/hubspot/email/ownership/rpc/ownedaddress/OwnedAddressRpc';
import { getConnectedAccountsForUserRpc } from '../../__generated__/chirp/com/hubspot/conversations/email/rpc/ConnectedAccountRpc';
const rpcClient = createRpcClientV2({
  hubHttpClient: http
});
function getQuery(query) {
  return Object.assign({
    state__in: ['automated_for_form', 'automated_for_form_draft']
  }, query);
}

/**
 * GET cosemail/v1/emails/{id}
 * @param {number} id Email id
 * @returns {Promise} fetch email by id
 */
export function fetchEmail(id, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get(`cosemail/v1/emails/${id}`);
}

/**
 * GET cosemail/v1/emails/{id}/buffer
 * @param {number} id Email id
 * @returns {Promise} fetch email by id
 */
export function fetchEmailBuffer(id, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get(`cosemail/v1/emails/${id}/buffer`);
}

/**
 * PUT cosemail/v1/emails/{id}/update-and-publish
 * @param {number} id Email id
 * @param {Object} emailData Email data
 * @returns {Promise} save email data by id
 */
export function saveEmail(emailData, id, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.put(`cosemail/v1/emails/${id}/update-and-publish`, {
    data: emailData
  });
}

/**
 * POST cosemail/v1/emails/create-plain-email
 * @param {Object} emailData Email data
 * @returns {Promise} created new email with initial email data
 */
export function createPlainEmail(emailData, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.post(`cosemail/v1/emails/create-plain-email`, {
    data: emailData
  });
}

/**
 * GET cosemail/v1/emails
 * @param {Object} query Query params
 * @returns {Promise} fetch paginated emails
 */
export function fetchEmails(query = {}, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get('cosemail/v1/emails', {
    query: getQuery(query)
  });
}

/**
 * GET cosemail/v1/emails/search/automated-with-ticket-and-deal
 * @param {Object} query Query params
 * @returns {Promise} fetch paginated automated emails
 */
export function fetchAutomatedEmails(query = {}, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get('cosemail/v1/emails/search/automated-with-ticket-and-deal', {
    query: Object.assign({}, query, {
      emailTypes: ['AUTOMATED_EMAIL', 'AUTOMATED_AB_EMAIL'],
      emailSortField: 'UPDATED',
      sortOrder: 'DESC'
    })
  });
}

/**
 * POST cosemail/v1/emails
 * @param {string} name Email name
 * @returns {Promise} create email
 */
export function createEmail(name, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.post('cosemail/v1/emails', {
    query: CREATE_DEFAULTS.emails.query,
    data: Object.assign({
      name
    }, CREATE_DEFAULTS.emails.data)
  });
}

/**
 * GET email-ownership/v1/owned-addresses
 */
export const fetchOwnedEmailAddresses = (...rest) => {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get('email-ownership/v1/owned-addresses');
};
export function fetchOwnedEmailAddressesChirp() {
  return rpcClient.call(listOwnedAddressesRpc, {
    request: {}
  }).then(response => {
    var _response$ownedAddres;
    return ((_response$ownedAddres = response.ownedAddresses) !== null && _response$ownedAddres !== void 0 ? _response$ownedAddres : []).map(({
      content,
      isVerified,
      type
    }) => ({
      content,
      isVerified,
      type: type
    }));
  });
}

/**
 * GET email-ownership/v1/owned-addresses
 */
export const fetchConnectedInboxAccounts = (...rest) => {
  if (Routes.isUngated(gate('CommPlatform::CHIRP::GetConnectAccountsForUser'))) {
    const {
      user: {
        user_id: userId,
        email: userEmail,
        first_name: userFirstName,
        last_name: userLastName
      }
    } = userInfoSync();
    return rpcClient.call(getConnectedAccountsForUserRpc, {
      request: {
        userId,
        userEmail,
        userFirstName,
        userLastName
      }
    }).then(({
      connectedAccounts
    }) => (connectedAccounts !== null && connectedAccounts !== void 0 ? connectedAccounts : []).map(account => {
      var _account$friendlyFrom, _account$resolvedFrom, _account$routingRules, _account$sendFromEmai, _account$ticketRulesI;
      return {
        agentId: account.agentId,
        appId: account.appId,
        channelInstanceId: account.channelInstanceId,
        connected: true,
        conversationsInboxName: account.conversationsInboxName,
        createdAt: account.createdAt,
        deletedAt: account.deletedAt,
        displayGlobally: account.displayGlobally,
        email: account.email,
        emailAccountType: account.emailAccountType,
        friendlyFromName: (_account$friendlyFrom = account.friendlyFromName) !== null && _account$friendlyFrom !== void 0 ? _account$friendlyFrom : '',
        id: account.id,
        inboxId: account.inboxId,
        includeAgentInFromName: account.includeAgentInFromName,
        isActive: account.isActive,
        isDefault: account.isDefault,
        isFacsimileInboxConnected: account.isFacsimileInboxConnected,
        portalId: account.portalId,
        resolvedFromName: (_account$resolvedFrom = account.resolvedFromName) !== null && _account$resolvedFrom !== void 0 ? _account$resolvedFrom : '',
        resolvedUserId: account.resolvedUserId,
        routingRulesId: (_account$routingRules = account.routingRulesId) !== null && _account$routingRules !== void 0 ? _account$routingRules : 0,
        sendFromEmail: (_account$sendFromEmai = account.sendFromEmail) !== null && _account$sendFromEmai !== void 0 ? _account$sendFromEmai : '',
        ticketRulesId: (_account$ticketRulesI = account.ticketRulesId) !== null && _account$ticketRulesI !== void 0 ? _account$ticketRulesI : null,
        userAllowedAccess: account.userAllowedAccess
      };
    }));
  }
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get('conversations-email/v1/connected-accounts/all');
};
export const fetchCanSpam = (...rest) => {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get('content/api/v4/can-spam-settings', {
    query: {
      casing: 'snake',
      limit: 500
    }
  });
};
export function convertLegacyFollowupEmail(email, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  const {
    id,
    language,
    name
  } = email;
  return httpClient.post(`cosemail/v1/emails/${id}/convert-forms-followup-to-automated`, {
    data: {
      language,
      name
    }
  });
}
export function fetchIsLegacyEmailConvertible(emailId, ...rest) {
  const httpClient = getHttpClientAsLastParam(rest);
  return httpClient.get(`cosemail/v1/emails/${emailId}/is-convertible-to-drag-and-drop`);
}